import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/format';

export default function TicketSelector({ tickets, onSelect }) {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState(() =>
    tickets.reduce((acc, t) => ({ ...acc, [t.id]: 0 }), {})
  );

  const updateQuantity = useCallback((ticketId, delta) => {
    setQuantities(prev => {
      const ticket = tickets.find(t => t.id === ticketId);
      const available = ticket.quantity - (ticket.sold || 0);
      const newQty = Math.max(0, Math.min(available, prev[ticketId] + delta));

      const newQuantities = { ...prev, [ticketId]: newQty };

      // Call onSelect with the new selection
      const selected = Object.entries(newQuantities)
        .filter(([_, qty]) => qty > 0)
        .map(([ticketTypeId, quantity]) => ({ ticketTypeId, quantity }));

      onSelect(selected);

      return newQuantities;
    });
  }, [tickets, onSelect]);

  const total = useMemo(() =>
    tickets.reduce((sum, t) => sum + t.price * quantities[t.id], 0),
    [tickets, quantities]
  );

  const totalQuantity = useMemo(() =>
    Object.values(quantities).reduce((a, b) => a + b, 0),
    [quantities]
  );

  return (
    <div className="space-y-3" role="group" aria-label="Ticket selection">
      {tickets.map((ticket) => {
        const available = ticket.quantity - (ticket.sold || 0);
        const soldOut = available === 0;
        const currentQty = quantities[ticket.id];

        return (
          <div
            key={ticket.id}
            className={`flex items-center justify-between p-4 border rounded-xl transition-all ${
              soldOut
                ? 'bg-gray-50 border-gray-200'
                : currentQty > 0
                  ? 'bg-brand-50 border-brand-200'
                  : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900">{ticket.name}</h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-semibold text-gray-900">
                  {formatPrice(ticket.price)}
                </span>
                <span className="text-sm text-gray-400">
                  {soldOut ? (
                    <span className="text-[#FF3B30]">{t('tickets.soldOut')}</span>
                  ) : available <= 10 ? (
                    <span className="text-[#FF9500]">{t('tickets.onlyLeft', { count: available })}</span>
                  ) : (
                    t('tickets.available', { count: available })
                  )}
                </span>
              </div>
            </div>

            {!soldOut && (
              <div className="flex items-center gap-2 ml-4" role="spinbutton" aria-label={`Quantity for ${ticket.name}`} aria-valuenow={currentQty} aria-valuemin={0} aria-valuemax={available}>
                <button
                  type="button"
                  onClick={() => updateQuantity(ticket.id, -1)}
                  disabled={currentQty === 0}
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-disabled disabled:cursor-not-allowed flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                  aria-label={`Decrease ${ticket.name} quantity`}
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="w-8 text-center font-semibold text-gray-900 tabular-nums" aria-live="polite">
                  {currentQty}
                </span>
                <button
                  type="button"
                  onClick={() => updateQuantity(ticket.id, 1)}
                  disabled={currentQty >= available}
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-disabled disabled:cursor-not-allowed flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                  aria-label={`Increase ${ticket.name} quantity`}
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Order Summary */}
      {total > 0 && (
        <div className="pt-4 mt-4 border-t border-gray-200" aria-live="polite">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-gray-600">{t('common.subtotal')}</span>
              <span className="text-sm text-gray-400 ml-1">
                ({totalQuantity} {totalQuantity !== 1 ? t('tickets.tickets') : t('tickets.ticket')})
              </span>
            </div>
            <span className="text-xl font-bold text-gray-900">
              {formatPrice(total)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
